import {Component} from 'react';
import languages from '../languages.js';
import './InternalLinkSuggestions.scss';
import LanguageInfo from './LanguageInfo.js';
import Loader from './Loader.js';
import Markup from '../lib/Markup.js';
import IconPadlock from './IconPadlock.js';
import CheckListError from './CheckListError.js';
import Fetch from '../lib/Fetch.js';

class InternalLinkSuggestions extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			error: '',
			language: languages.find((lang) => lang.langCode === this.props.appSettings.language),
			location: this.props.location,
			linkSuggestions: [],
		}

		this.handleButtonClick = this.handleButtonClick.bind(this);
		this.handleAddLinkButtonClick = this.handleAddLinkButtonClick.bind(this);
	}

	componentDidUpdate() {
		if (this.props.appSettings.language !== this.state.language.langCode) {
			this.setState({
				language: languages.find((lang) => lang.langCode === this.props.appSettings.language),
			})
		}

		if (this.props.appSettings.location !== this.state.location) {
			this.setState({
				location: this.props.appSettings.location,
			});
		}
	}

	handleAddLinkButtonClick(event) {
		const {word, url} = event.target.dataset;
		this.insertLink(word, url);
	}

	handleButtonClick(event) {
		event.preventDefault();
		this.getLinkSuggestions();
	}

	insertLink(linkedWord, url) {
		const markup = document.createElement('div');
		markup.innerHTML = this.props.content;

		let elements = markup.querySelectorAll('dd, li, p');
		if (!elements) {
			return;
		}
		let done = false;
		let linkAdded = false;
		elements.forEach((el) => {
			if (done) {
				return;
			}

			let html = el.innerHTML;
			let htmlLower = html.toLocaleLowerCase();
			let wordStart = -1;

			let regex = new RegExp("\\b" + linkedWord + "\\b((?!\\W(?=\\w))|(?=\\s))", "gi");
			wordStart = htmlLower.search(regex);

			if (wordStart === -1) {
				return;
			}
			let wordEnd = wordStart + linkedWord.length;
			let linkEnd = htmlLower.indexOf('</a>', wordEnd);
			let isLinked = linkEnd >= 0;
			if (isLinked) {
				let between = htmlLower.substring(wordEnd, linkEnd + wordEnd);
				if (between.indexOf('<a') !== -1) {
					isLinked = false;
				}
			}

			if (isLinked) {
				done = true;
				return;
			}

			let before = html.substring(0, wordStart);
			let word = html.substring(wordStart, wordEnd);
			let after = html.substring(wordEnd);
			html = `${before}<a href="${url}">${word}</a>${after}`;
			el.innerHTML = html;
			done = true;
			linkAdded = true;
			if (markup.innerHTML !== this.props.content) {
				this.props.contentCallback(markup.innerHTML);
			}
		});

		if (!linkAdded) {
			alert(`"${linkedWord}" ${window?.t?.WORD_NOT_FOUND_IN_TEXT}`);
		} else {
			alert(window?.t?.LINK_ADDED);
		}
	}

	async getLinkSuggestions() {
		const {isPro} = this.props;
		if (!isPro) {
			alert(window?.t?.FEATURE_AVAILABLE_IN_PAID);
			return;
		}
		if (!this.props.siteUrl) {
			this.setState({
				error: window?.t?.ERR_NO_URL,
			});
			return;
		}

		let isUrlValid = true;
		try {
			new URL(this.props.siteUrl);
		} catch(err) {
			isUrlValid = false;
		}
		if (!isUrlValid) {
			this.setState({
				error: window?.t?.ERR_URL_INVALID,
			});
			return;
		}

		let priceId = 'content-link-suggestions';
		const canProceed = await this.props.limitUsageCallback('content-editor-ai-internal-links', priceId);
		if (!canProceed) {
			this.setState({
				loading: false,
				error: window?.t?.ERR_LIMIT_REACHED_NO_MORE_CREDITS,
			});
			return;
		}

		this.setState({
			loading: window?.t?.FETCHING_LINK_SUGGESTIONS,
			error: '',
		});
		let searchVol = [];
		let results = [];

		let keywords = await this.fetchKeywords(this.props.content) || [];
		if (keywords.length && this.props.focusKeyword) {
			keywords = keywords.filter((keyword) => keyword.toLowerCase() !== this.props.focusKeyword.toLowerCase());
		}

		if (!keywords.length) {
			this.setState({
				loading: false,
				error: keywords && keywords.error ? keywords.error.message : window?.t?.ERR_NO_KEYWORDS_FOUND,
			});
			return;
		}

		const searchVolData = await this.fetchSearchVolume(keywords, this.state.language.langCode);
		if (searchVolData && searchVolData.status !== 'ok') {
			this.setState({
				loading: false,
				error: searchVolData.message,
			});
		} else if (searchVolData) {
			searchVol = searchVolData.data.sort((a, b) => {
				return (a.searchVolume - b.searchVolume) * -1;
			});
			searchVol = searchVol.filter((item) => !!item.searchVolume);
			searchVol = searchVol.splice(0, 50);
		}

		if (!searchVol.length) {
			this.setState({
				loading: false,
				error: window?.t?.ERR_SV,
			});
			return;
		}
		results = searchVol;
		this.setState({
			linkSuggestions: results
		});

		if (this.props.siteUrl && searchVol.length) {
			let requestCount = searchVol.length;

			searchVol.forEach((svItem) => {
				const data = new FormData();
				data.append('keywords', JSON.stringify([svItem.keyword]));
				data.append('website', this.props.siteUrl);
				data.append('language', this.state.language.id);
				data.append('location', this.state.location);
				fetch('/tools/api/get-link-suggestions.php', {
					method: 'POST',
					body: data
				})
				.then((response) => {
					if (response.status < 400) {
						return response.json();
					}
					return {
						status: 'error',
					}
				})
				.then((respData) => {
					if (respData.status === 'ok' && respData.data.length) {
						let index = results.findIndex((item) => item.keyword === respData.data[0].keyword);
						if (index > -1) {
							results[index].url = respData.data[0].url;
						}
						this.setState({
							linkSuggestions: results
						})
					}
					requestCount --;
					if (!requestCount) {
						this.setState({
							loading: false,
						});
					}
				})
				.catch((error) => console.error(error));
			})
		}
	}

	async fetchKeywords(text) {
		text = text.replaceAll('<p><br></p>', '');
		text = text.replaceAll('><', '> <');
		const markup = document.createElement('div');
		markup.innerHTML = text.replaceAll('><', '> <');

		return await Fetch.post(`https://${window.location.hostname}/tools/content/ajax/keywords-from-text.php`, {
			text: markup.textContent,
		});
	}

	async fetchSearchVolume(keywords, language) {
		const response = await Fetch.post(`https://${window.location.hostname}/tools/api/get-search-volume.php`, {
			phrases: keywords,
			language: language,
		});
		return response;
	}

	render () {
		const {isPro} = this.props;

		const loadingInfo = this.state.loading
			? (<p className="waiting">{this.state.loading}</p>)
			: isPro
				? (<p className="main-button"><button className="button primary" id="get-suggestions" onClick={this.handleButtonClick}>{window?.t?.GENERATE_LINK_SUGGESTIONS}</button></p>)
				: (<p className="main-button"><button className="button primary has-tooltip" id="get-suggestions" onClick={this.handleButtonClick}>{window?.t?.GENERATE_LINK_SUGGESTIONS}<span className="tooltip"><IconPadlock />{window?.t?.AVAILABLE_IN_ADVANCED}</span></button></p>);
		const error = this.state.error ? (<CheckListError text={this.state.error} />) : null;
		let {linkSuggestions} = this.state;

		let suggestions = '';
		if (!error && linkSuggestions.length) {
			let suggestionData = linkSuggestions.map((item, index) => {
				let key = 'link-suggestion-' + index;
				const urlContent = item.url
				? (
					<>
						<a href={item.url} target="_blank" className="suggested-link" rel="noreferrer">{item.url}</a><br />
						<button className="button secondary data-action-button" data-word={item.keyword} data-url={item.url} onClick={this.handleAddLinkButtonClick}>{window?.t?.ADD_LINK}</button>
					</>
				)
				: this.state.loading ? (<Loader />) : 'N/A';

				return (
					<tr key={key}>
						<td>{item.keyword}</td>
						<td>{item.searchVolume}</td>
						<td>{urlContent}</td>
					</tr>
				)
			});

			suggestions = (
				<div className="table">
					<table>
						<thead>
							<tr>
								<th>{window?.t?.KEYWORD}</th>
								<th>{window?.t?.SEARCH_VOLUME}</th>
								<th>{window?.t?.SUGGESTED_LINK}</th>
							</tr>
						</thead>
						<tbody>
							{suggestionData}
						</tbody>
					</table>
				</div>
			);
		}

		return (
			<>
			<h4>{window?.t?.INTERNAL_LINK_SUGGESTIONS}</h4>
			<LanguageInfo
				language={this.state.language}
				location={this.state.location}
				toggleSettingsCallback={this.props.toggleSettingsCallback}
			/>
			{error}
			{loadingInfo}
			{suggestions}
			</>
		)
	}
}

export default InternalLinkSuggestions;